import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { estimateDowntimeCost, calculateLossScenarios, formatCurrency, calculateRuinProbability, getRuinRiskLevel } from '../../../shared/utils/impactCalibration'
import { simulateScenario } from '../../../shared/utils/monteCarloSimulation'
import { supabase } from '../../../shared/lib/supabase'
import threatProfiles from '../../../shared/data/threatProfiles.json'
import SensitiveDataProfile from './SensitiveDataProfile'
import CyberInsurancePoliciesSection from './CyberInsurancePoliciesSection'
import { resolveThreatProfile } from '../../../shared/utils/resolveThreatProfile'

// Common cyber insurance carriers
const INSURANCE_CARRIERS = [
  { value: '', label: 'Select carrier...' },
  { value: 'coalition', label: 'Coalition' },
  { value: 'corvus', label: 'Corvus Insurance' },
  { value: 'chubb', label: 'Chubb' },
  { value: 'travelers', label: 'Travelers' },
  { value: 'hartford', label: 'The Hartford' },
  { value: 'beazley', label: 'Beazley' },
  { value: 'aig', label: 'AIG' },
  { value: 'hiscox', label: 'Hiscox' },
  { value: 'cowbell', label: 'Cowbell Cyber' },
  { value: 'at-bay', label: 'At-Bay' },
  { value: 'axis', label: 'AXIS Insurance' },
  { value: 'zurich', label: 'Zurich' },
  { value: 'liberty-mutual', label: 'Liberty Mutual' },
  { value: 'nationwide', label: 'Nationwide' },
  { value: 'tokio-marine', label: 'Tokio Marine HCC' },
  { value: 'other', label: 'Other' }
]

// Icon components for scenarios
const ScenarioIcons = {
  'currency-dollar': (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  'lock-closed': (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  ),
  'shield-exclamation': (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.618 5.984A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016zM12 9v2m0 4h.01" />
    </svg>
  ),
  'server': (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2m-2-4h.01M17 16h.01" />
    </svg>
  ),
  'link': (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
    </svg>
  ),
  'scale': (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
    </svg>
  ),
  'user-minus': (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 00-6 6v1h12v-1a6 6 0 00-6-6zM21 12h-6" />
    </svg>
  )
}

// Severity color mappings
const getSeverityColors = (level) => {
  switch (level) {
    case 'Critical':
      return {
        bg: 'bg-red-50 dark:bg-red-900/20',
        border: 'border-red-200 dark:border-red-800',
        icon: 'text-red-600 dark:text-red-400',
        badge: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300',
        accent: 'text-red-600 dark:text-red-400'
      }
    case 'High':
      return {
        bg: 'bg-orange-50 dark:bg-orange-900/20',
        border: 'border-orange-200 dark:border-orange-800',
        icon: 'text-orange-600 dark:text-orange-400',
        badge: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300',
        accent: 'text-orange-600 dark:text-orange-400'
      }
    case 'Medium':
      return {
        bg: 'bg-yellow-50 dark:bg-yellow-900/20',
        border: 'border-yellow-200 dark:border-yellow-800',
        icon: 'text-yellow-600 dark:text-yellow-500',
        badge: 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300',
        accent: 'text-yellow-600 dark:text-yellow-400'
      }
    default:
      return {
        bg: 'bg-green-50 dark:bg-green-900/20',
        border: 'border-green-200 dark:border-green-800',
        icon: 'text-green-600 dark:text-green-400',
        badge: 'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300',
        accent: 'text-green-600 dark:text-green-400'
      }
  }
}

// Expandable Scenario Card Component
function ScenarioCard({ scenario, hasInsurance, isExpanded, onToggle, isSelected, onSelectToggle }) {
  const colors = getSeverityColors(scenario.severity.level)
  const icon = ScenarioIcons[scenario.icon] || ScenarioIcons['shield-exclamation']

  // Format component names for display
  const formatComponentName = (key) => {
    return key
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .replace(/Cost$/, '')
      .replace(/Base$/, '')
      .trim()
  }

  return (
    <div className={`rounded-lg border ${colors.border} ${colors.bg} overflow-hidden transition-all duration-200 ${isSelected === false ? 'opacity-50' : ''}`}>
      {/* Collapsed Header - Always Visible */}
      <div className="flex items-center">
        {/* Checkbox */}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            e.preventDefault()
            if (onSelectToggle) {
              onSelectToggle(scenario.scenarioId)
            }
          }}
          className="pl-4 pr-2 py-4 flex items-center cursor-pointer"
        >
          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
            isSelected
              ? 'bg-primary-600 border-primary-600'
              : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500'
          }`}>
            {isSelected && (
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
              </svg>
            )}
          </div>
        </button>

        <button
          onClick={onToggle}
          className="flex-1 p-4 pl-0 flex items-center justify-between hover:bg-white/50 dark:hover:bg-gray-800/50 transition-colors"
        >
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${colors.bg} ${colors.icon}`}>
              {icon}
            </div>
            <div className="text-left">
              <div className="flex items-center space-x-2">
                <h4 className="font-semibold text-gray-900 dark:text-white">
                  {scenario.scenarioName}
                </h4>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colors.badge}`}>
                  {scenario.severity.level}
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {scenario.coverageType}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-4">
            {/* Key Metrics - Compact View */}
            <div className="hidden sm:flex items-center space-x-4 text-sm">
              <div className="text-right">
                <div className="text-xs text-gray-500 dark:text-gray-400">SLE</div>
                <div className="font-medium text-gray-900 dark:text-white">{formatCurrency(scenario.distribution?.sleP50 || scenario.grossLoss)}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-gray-500 dark:text-gray-400">ALE</div>
                <div className={`font-bold ${colors.accent}`}>{formatCurrency(scenario.distribution?.aleP50 || scenario.ale)}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-gray-500 dark:text-gray-400">Prob</div>
                <div className="font-medium text-gray-900 dark:text-white">{scenario.probabilityPercent}%</div>
              </div>
            </div>

            {/* Expand/Collapse Arrow */}
            <svg
              className={`w-5 h-5 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </button>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-gray-200/50 dark:border-gray-700/50">
          {/* Description */}
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-3 mb-4">
            {scenario.detailedDescription || scenario.description}
          </p>

          {/* Mobile Key Metrics */}
          <div className="sm:hidden grid grid-cols-3 gap-2 mb-4">
            <div className="p-2 bg-white/50 dark:bg-gray-800/50 rounded-lg text-center">
              <div className="text-xs text-gray-500 dark:text-gray-400">SLE (P50)</div>
              <div className="text-base font-bold text-gray-900 dark:text-white">{formatCurrency(scenario.distribution?.sleP50 || scenario.grossLoss)}</div>
            </div>
            <div className="p-2 bg-white/50 dark:bg-gray-800/50 rounded-lg text-center">
              <div className="text-xs text-gray-500 dark:text-gray-400">ALE (P50)</div>
              <div className={`text-base font-bold ${colors.accent}`}>{formatCurrency(scenario.distribution?.aleP50 || scenario.ale)}</div>
            </div>
            <div className="p-2 bg-white/50 dark:bg-gray-800/50 rounded-lg text-center">
              <div className="text-xs text-gray-500 dark:text-gray-400">Prob</div>
              <div className="text-base font-bold text-gray-900 dark:text-white">{scenario.probabilityPercent}%</div>
            </div>
          </div>

          {/* Loss Range — conditional on incident occurring */}
          {scenario.distribution && (
            <div className="mb-4 p-4 bg-white/70 dark:bg-gray-800/70 rounded-lg">
              <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-1">
                If This Incident Occurs
              </p>
              <p className="text-[11px] text-gray-400 dark:text-gray-500 mb-4">
                {scenario.probabilityPercent}% annual probability, loss range based on 5,000 Monte Carlo simulations
              </p>

              {/* Range visualization with aligned labels */}
              {(() => {
                const d = scenario.distribution
                const max = d.sleP95 || 1
                const positions = {
                  p25: Math.max(0, Math.min(100, ((d.sleP25 || 0) / max) * 100)),
                  p50: Math.max(0, Math.min(100, ((d.sleP50 || 0) / max) * 100)),
                  p90: Math.max(0, Math.min(100, ((d.sleP90 || 0) / max) * 100)),
                  p95: 100
                }
                return (
                  <div className="relative">
                    {/* Gradient bar */}
                    <div className="relative h-6 rounded-lg overflow-hidden" style={{ background: 'linear-gradient(90deg, #D1FAE5, #FEF3C7 50%, #FEE2E2 80%, #FECACA)' }}>
                      {/* P50 marker line */}
                      <div className="absolute top-0 h-full w-0.5 bg-gray-800 dark:bg-white" style={{ left: `${positions.p50}%`, transform: 'translateX(-50%)' }} />
                    </div>

                    {/* Position-aligned labels */}
                    <div className="relative h-12 mt-1">
                      {[
                        { key: 'p25', value: d.sleP25, label: 'Best case', color: 'text-green-700 dark:text-green-400', align: 'left' },
                        { key: 'p50', value: d.sleP50, label: 'Most likely', color: 'text-gray-900 dark:text-white', align: 'center' },
                        { key: 'p90', value: d.sleP90, label: 'Severe', color: 'text-amber-700 dark:text-amber-400', align: 'center' },
                        { key: 'p95', value: d.sleP95, label: 'Worst case', color: 'text-red-700 dark:text-red-400', align: 'right' }
                      ].map(item => (
                        <div
                          key={item.key}
                          className="absolute"
                          style={{
                            left: `${positions[item.key]}%`,
                            transform: item.align === 'left' ? 'translateX(0)' : item.align === 'right' ? 'translateX(-100%)' : 'translateX(-50%)'
                          }}
                        >
                          <p className={`text-xs font-bold ${item.color} whitespace-nowrap`}>{formatCurrency(item.value)}</p>
                          <p className="text-[10px] text-gray-400 dark:text-gray-500 whitespace-nowrap">{item.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )
              })()}
            </div>
          )}

          {/* Financial Details Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div className="p-3 bg-white/70 dark:bg-gray-800/70 rounded-lg">
              <div className="text-xs text-gray-500 dark:text-gray-400">Single Loss (P50)</div>
              <div className="text-sm font-bold text-gray-900 dark:text-white">
                {formatCurrency(scenario.distribution?.sleP50 || scenario.grossLoss)}
              </div>
            </div>
            {hasInsurance && (
              <>
                <div className="p-3 bg-white/70 dark:bg-gray-800/70 rounded-lg">
                  <div className="text-xs text-gray-500 dark:text-gray-400">Insurance Payout</div>
                  <div className="text-sm font-bold text-green-600 dark:text-green-400">
                    -{formatCurrency(scenario.insurance?.insurancePayout || 0)}
                  </div>
                </div>
                <div className="p-3 bg-white/70 dark:bg-gray-800/70 rounded-lg">
                  <div className="text-xs text-gray-500 dark:text-gray-400">Net Exposure (P90)</div>
                  <div className="text-sm font-bold text-gray-900 dark:text-white">
                    {formatCurrency(scenario.distribution?.netP90 || scenario.netExposure)}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Cost Components Breakdown */}
          <div className="bg-white/50 dark:bg-gray-800/50 rounded-lg p-3">
            <h5 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
              Cost Components
            </h5>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {Object.entries(scenario.components || {}).map(([key, value]) => {
                if (typeof value !== 'number' || value === 0) return null
                return (
                  <div key={key} className="flex justify-between text-xs">
                    <span className="text-gray-500 dark:text-gray-400">
                      {formatComponentName(key)}
                    </span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {formatCurrency(value)}
                    </span>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Context Factors */}
          {scenario.contextFactors && scenario.contextFactors.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1">
              {scenario.contextFactors.map((factor, idx) => (
                <span
                  key={idx}
                  className="text-xs px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded"
                >
                  {factor.replace(/_/g, ' ')}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function ImpactCalibrationStep({ responses, onSave, maturityScore, client, showOnly = null, selectedScenarioIds = null, onSelectionChange = null }) {
  const [sensitiveDataVolumes, setSensitiveDataVolumes] = useState(null)

  const handleVolumesChange = useCallback((volumes) => {
    setSensitiveDataVolumes(volumes)
  }, [])

  const [impactData, setImpactData] = useState({
    estimated_annual_revenue: '',
    employee_count: '',
    data_record_count: '',
    recovery_time_objective: '',
    has_cyber_insurance: false,
    insurance_carrier: '',
    insurance_carrier_other: '',
    insurance_coverage_limit: '',
    client_retention: '',
    downtime_cost_per_hour: '',
    cyber_reserves: '',
    ruin_time_horizon: '3',
    recommendation_exceedance_target: '0.05'
  })

  const [showDowntimeHelper, setShowDowntimeHelper] = useState(false)
  const [impactTab, setImpactTab] = useState('data')
  const [expandedScenario, setExpandedScenario] = useState(null)
  // Use object instead of Set for better React change detection
  // When controlled by parent (selectedScenarioIds prop), use that; otherwise local state
  const [localSelectedScenarios, setLocalSelectedScenarios] = useState({})
  const selectedScenarios = selectedScenarioIds || localSelectedScenarios
  const setSelectedScenarios = (val) => {
    const newVal = typeof val === 'function' ? val(selectedScenarios) : val
    setLocalSelectedScenarios(newVal)
    if (onSelectionChange) onSelectionChange(newVal)
  }

  // Critical systems for BIA-informed downtime
  const [criticalSystems, setCriticalSystems] = useState([])
  useEffect(() => {
    if (!client?.id) return
    supabase.from('client_critical_systems').select('*').eq('client_id', client.id)
      .then(({ data }) => setCriticalSystems((data || []).filter(s => s.hourly_cost_impact != null)))
  }, [client?.id])

  // Weighted aggregate hourly cost from critical systems
  const systemsDerivedCost = useMemo(() => {
    if (criticalSystems.length === 0) return null
    const totalRevDep = criticalSystems.reduce((sum, s) => sum + (s.revenue_dependency_pct || 0), 0)
    if (totalRevDep === 0) {
      // Simple average if no revenue dependency data
      return Math.round(criticalSystems.reduce((sum, s) => sum + (s.hourly_cost_impact || 0), 0) / criticalSystems.length)
    }
    // Revenue-dependency-weighted average
    const weighted = criticalSystems.reduce((sum, s) => {
      const weight = (s.revenue_dependency_pct || 0) / totalRevDep
      return sum + (s.hourly_cost_impact || 0) * weight
    }, 0)
    return Math.round(weighted)
  }, [criticalSystems])

  // Track initialization to prevent auto-save during initial load
  const isInitializing = useRef(true)

  // Load saved impact data
  useEffect(() => {
    const savedData = responses?.impactData
    if (savedData) {
      setImpactData(prev => ({
        ...prev,
        ...savedData,
        // Backwards compatibility: map old insurance_deductible to client_retention
        client_retention: savedData.client_retention ?? savedData.insurance_deductible ?? '',
        insurance_carrier: savedData.insurance_carrier ?? '',
        insurance_carrier_other: savedData.insurance_carrier_other ?? '',
        cyber_reserves: savedData.cyber_reserves ?? '',
        ruin_time_horizon: savedData.ruin_time_horizon ?? '3',
        recovery_time_objective: savedData.recovery_time_objective ?? '',
        recommendation_exceedance_target: savedData.recommendation_exceedance_target ?? '0.05'
      }))
    }
  }, [responses?.impactData])

  // Resolve threat profile via shared cascade: saved → industry-recommended → TP-DEFAULT.
  // This means MPT (Real Estate / REIT) gets the right profile even without
  // anyone visiting a Threat Context page first.
  const selectedProfile = useMemo(() => {
    const { profile } = resolveThreatProfile({ client, responses })
    return profile
  }, [client, responses])

  // Extract org characteristics and frameworks from business scoping
  const { orgCharacteristics, frameworks } = useMemo(() => {
    const bs = responses?.businessScoping || {}
    const chars = []
    const fwks = []

    // Extract org characteristics
    if (bs['bs-6']?.value === 'yes' || bs['bs-6'] === 'yes') chars.push('publicly-traded')
    if (bs['bs-7']?.value === 'yes' || bs['bs-7'] === 'yes') chars.push('government-contractor')
    if (bs['bs-8']) chars.push('processes-payments')

    // Extract frameworks from business scoping selections
    const frameworkResponse = bs['bs-11'] || bs['bs-12']
    if (frameworkResponse) {
      const selectedFrameworks = Array.isArray(frameworkResponse)
        ? frameworkResponse
        : (frameworkResponse.value ? [frameworkResponse.value] : [])
      selectedFrameworks.forEach(f => {
        if (f.toLowerCase().includes('hipaa')) fwks.push('hipaa')
        if (f.toLowerCase().includes('pci')) fwks.push('pci-dss')
        if (f.toLowerCase().includes('gdpr')) fwks.push('gdpr')
        if (f.toLowerCase().includes('ccpa') || f.toLowerCase().includes('cpra')) fwks.push('ccpa')
        if (f.toLowerCase().includes('cmmc') || f.toLowerCase().includes('nist 800-171')) fwks.push('cmmc')
        if (f.toLowerCase().includes('sox')) fwks.push('sox')
      })
    }

    return { orgCharacteristics: chars, frameworks: fwks }
  }, [responses?.businessScoping])

  // Calculate estimated downtime cost
  const estimatedDowntime = useMemo(() => {
    if (impactData.estimated_annual_revenue || impactData.employee_count) {
      return estimateDowntimeCost({
        estimated_annual_revenue: parseFloat(impactData.estimated_annual_revenue) || 0,
        employee_count: parseInt(impactData.employee_count) || 0
      })
    }
    return null
  }, [impactData.estimated_annual_revenue, impactData.employee_count])

  // Calculate loss scenario preview with full context
  const lossPreview = useMemo(() => {
    const numericData = {
      estimated_annual_revenue: parseFloat(impactData.estimated_annual_revenue) || 0,
      employee_count: parseInt(impactData.employee_count) || 0,
      data_record_count: parseInt(impactData.data_record_count) || 0,
      has_cyber_insurance: impactData.has_cyber_insurance,
      insurance_coverage_limit: parseFloat(impactData.insurance_coverage_limit) || 0,
      client_retention: parseFloat(impactData.client_retention) || 0,
      downtime_cost_per_hour: parseFloat(impactData.downtime_cost_per_hour) || 0,
      criticalSystems: criticalSystems.length > 0 ? criticalSystems : undefined
    }

    if (numericData.estimated_annual_revenue > 0 || numericData.employee_count > 0) {
      const result = calculateLossScenarios(
        numericData,
        responses?.threatContext,
        selectedProfile,
        maturityScore || 50,
        responses?.businessScoping,
        orgCharacteristics,
        frameworks,
        sensitiveDataVolumes,
        responses?.maturityAssessment
      )

      // Run per-scenario Monte Carlo simulations to get distribution ranges
      if (result?.scenarios) {
        result.scenarios = result.scenarios.map(scenario => {
          try {
            const sim = simulateScenario({
              scenarioId: scenario.scenarioId,
              scenarioName: scenario.scenarioName,
              probability: scenario.probability,
              grossLoss: scenario.grossLoss,
              insuranceOffset: scenario.insurance?.insurancePayout || 0
            }, 5000) // 5K iterations for speed
            // Use givenOccurrence (loss conditional on incident happening) for meaningful ranges
            const go = sim.givenOccurrence?.percentiles || sim.distribution.percentiles
            return {
              ...scenario,
              distribution: {
                sleP25: go.p25,
                sleP50: go.p50,
                sleP75: go.p75,
                sleP90: go.p90,
                sleP95: go.p95,
                occurrenceRate: sim.occurrenceRate,
                aleP50: Math.round((go.p50 || 0) * scenario.probability),
                aleP90: Math.round((go.p90 || 0) * scenario.probability),
                aleP95: Math.round((go.p95 || 0) * scenario.probability),
                netP50: sim.netExposure.p50,
                netP90: sim.netExposure.p90,
                netP95: sim.netExposure.p95
              }
            }
          } catch {
            return scenario
          }
        })
      }

      return result
    }
    return null
  }, [impactData, responses?.threatContext, responses?.businessScoping, responses?.maturityAssessment, selectedProfile, maturityScore, orgCharacteristics, frameworks, sensitiveDataVolumes, criticalSystems])

  // Create a stable key for scenario IDs to use as dependency
  const scenarioIdsKey = useMemo(() => {
    if (!lossPreview?.scenarios) return ''
    return lossPreview.scenarios.map(s => s.scenarioId).sort().join(',')
  }, [lossPreview?.scenarios])

  // Initialize selected scenarios when scenarios first become available
  // Skip if parent is controlling state (selectedScenarioIds prop)
  useEffect(() => {
    if (selectedScenarioIds) return // Parent controls selection
    if (scenarioIdsKey) {
      const scenarioIds = scenarioIdsKey.split(',').filter(Boolean)
      const selected = {}
      scenarioIds.forEach(id => { selected[id] = true })
      setLocalSelectedScenarios(selected)
      if (onSelectionChange) onSelectionChange(selected)
    }
  }, [scenarioIdsKey, selectedScenarioIds])

  // Calculate summary based on selected scenarios only
  const selectedSummary = useMemo(() => {
    if (!lossPreview?.scenarios) return null

    const selected = lossPreview.scenarios.filter(s => selectedScenarios[s.scenarioId])
    if (selected.length === 0) {
      return {
        totalGrossALE: 0,
        totalNetALE: 0,
        maxSingleLoss: 0,
        insuranceGap: 0,
        selectedCount: 0,
        totalCount: lossPreview.scenarios.length
      }
    }

    // Use correct property names from impactCalibration.js:
    // - ale: Annualized Loss Expectancy (gross)
    // - annualizedNetExposure: ALE after insurance
    // - grossLoss: Single Loss Expectancy
    // - insurance.netExposure: Single loss after insurance payout
    const totalGrossALE = selected.reduce((sum, s) => sum + (s.distribution?.aleP50 || s.ale || 0), 0)
    const totalNetALE = selected.reduce((sum, s) => sum + (s.annualizedNetExposure || s.distribution?.aleP50 || s.ale || 0), 0)
    const maxSingleLoss = Math.max(...selected.map(s => s.distribution?.sleP90 || s.grossLoss || 0))

    // Calculate insurance gap: max loss minus coverage limit (if insured)
    const coverageLimit = impactData.has_cyber_insurance ? (parseFloat(impactData.insurance_coverage_limit) || 0) : 0
    const insuranceGap = Math.max(0, maxSingleLoss - coverageLimit)

    return {
      totalGrossALE,
      totalNetALE,
      maxSingleLoss,
      insuranceGap,
      selectedCount: selected.length,
      totalCount: lossPreview.scenarios.length
    }
  }, [lossPreview?.scenarios, selectedScenarios, impactData.has_cyber_insurance, impactData.insurance_coverage_limit])

  // Calculate Ruin Theory analysis
  const ruinAnalysis = useMemo(() => {
    if (!selectedSummary || selectedSummary.totalGrossALE <= 0) return null

    return calculateRuinProbability({
      totalGrossALE: selectedSummary.totalGrossALE,
      maxSingleLoss: selectedSummary.maxSingleLoss,
      cyberBudget: parseFloat(impactData.cyber_reserves) || 0,
      insuranceCoverage: parseFloat(impactData.insurance_coverage_limit) || 0,
      clientRetention: parseFloat(impactData.client_retention) || 0,
      timeHorizonYears: parseInt(impactData.ruin_time_horizon) || 3,
      annualRevenue: parseFloat(impactData.estimated_annual_revenue) || 0,
      hasInsurance: impactData.has_cyber_insurance
    })
  }, [selectedSummary, impactData.cyber_reserves, impactData.insurance_coverage_limit, impactData.client_retention, impactData.ruin_time_horizon, impactData.estimated_annual_revenue, impactData.has_cyber_insurance])

  // Get ruin risk level for display
  const ruinRiskLevel = useMemo(() => {
    if (!ruinAnalysis?.canCalculate) return null
    return getRuinRiskLevel(ruinAnalysis.ruinProbability)
  }, [ruinAnalysis])

  // Toggle scenario selection
  const handleSelectToggle = (scenarioId) => {
    setSelectedScenarios(prev => {
      const newState = { ...prev }
      if (newState[scenarioId]) {
        delete newState[scenarioId]
      } else {
        newState[scenarioId] = true
      }
      return newState
    })
  }

  // Select/Deselect all
  const handleSelectAll = () => {
    if (lossPreview?.scenarios) {
      const currentCount = Object.keys(selectedScenarios).length
      if (currentCount === lossPreview.scenarios.length) {
        setSelectedScenarios({})
      } else {
        const allSelected = {}
        lossPreview.scenarios.forEach(s => { allSelected[s.scenarioId] = true })
        setSelectedScenarios(allSelected)
      }
    }
  }

  const handleInputChange = (field, value) => {
    const newData = { ...impactData, [field]: value }
    setImpactData(newData)
  }

  const handleSave = () => {
    const numericData = {
      estimated_annual_revenue: parseFloat(impactData.estimated_annual_revenue) || null,
      employee_count: parseInt(impactData.employee_count) || null,
      data_record_count: parseInt(impactData.data_record_count) || null,
      recovery_time_objective: impactData.recovery_time_objective || null,
      has_cyber_insurance: impactData.has_cyber_insurance,
      insurance_carrier: impactData.has_cyber_insurance ? impactData.insurance_carrier : null,
      insurance_carrier_other: impactData.has_cyber_insurance && impactData.insurance_carrier === 'other' ? impactData.insurance_carrier_other : null,
      insurance_coverage_limit: impactData.has_cyber_insurance ? (parseFloat(impactData.insurance_coverage_limit) || null) : null,
      client_retention: impactData.has_cyber_insurance ? (parseFloat(impactData.client_retention) || null) : null,
      downtime_cost_per_hour: parseFloat(impactData.downtime_cost_per_hour) || null,
      cyber_reserves: parseFloat(impactData.cyber_reserves) || null,
      ruin_time_horizon: parseInt(impactData.ruin_time_horizon) || 3,
      recommendation_exceedance_target: parseFloat(impactData.recommendation_exceedance_target) || 0.05
    }
    onSave('impactData', numericData)
  }

  // Mark initialization complete after first render cycle
  useEffect(() => {
    const timer = setTimeout(() => {
      isInitializing.current = false
    }, 500)
    return () => clearTimeout(timer)
  }, [])

  // Auto-save when values change (skip during initialization)
  useEffect(() => {
    if (isInitializing.current) return

    const timer = setTimeout(() => {
      handleSave()
    }, 500)
    return () => clearTimeout(timer)
  }, [impactData])

  const applyEstimatedDowntime = () => {
    if (estimatedDowntime) {
      setImpactData(prev => ({
        ...prev,
        downtime_cost_per_hour: estimatedDowntime.estimated.toString()
      }))
      setShowDowntimeHelper(false)
    }
  }

  // Format number with commas for display in input fields
  const formatNumberWithCommas = (value) => {
    if (!value && value !== 0) return ''
    const numStr = String(value).replace(/[^\d]/g, '')
    if (!numStr) return ''
    return Number(numStr).toLocaleString('en-US')
  }

  // Parse formatted input back to raw number string
  const parseFormattedNumber = (formattedValue) => {
    return formattedValue.replace(/,/g, '')
  }

  const toggleScenario = (scenarioId) => {
    setExpandedScenario(expandedScenario === scenarioId ? null : scenarioId)
  }

  return (
    <div className="space-y-6">
      {/* Introduction — hidden in showOnly mode */}
      {!showOnly && (
      <div className="card p-6">
        <div className="border-b border-gray-100 dark:border-gray-700/30 pb-3">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Impact Calibration</h3>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            Provide financial and operational context to calculate realistic loss scenarios
          </p>
        </div>
      </div>
      )}

      {/* Tabbed input sections — shown on Impact Calibration page (showOnly=null) */}
      {!showOnly && (
      <>
      {/* Tab bar */}
      <div className="border-b border-gray-200 dark:border-gray-700 mt-2">
        <nav className="flex gap-6">
          {[
            { id: 'data', label: 'Data Profile & Catalog' },
            { id: 'financial', label: 'Downtime Analysis' },
            { id: 'insurance', label: 'Cyber Insurance' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setImpactTab(tab.id)}
              className={`pb-3 text-sm font-medium transition-colors relative ${
                impactTab === tab.id
                  ? 'text-gray-900 dark:text-white'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              {tab.label}
              {impactTab === tab.id && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-600 dark:bg-primary-400 rounded-full" />
              )}
            </button>
          ))}
        </nav>
      </div>

      <div className="pt-4" />

      {/* Sensitive Data Profile tab */}
      {impactTab === 'data' && (
      <SensitiveDataProfile
        clientId={client?.id}
        assessmentId={(() => {
          const assessments = client?.assessments || []
          const completed = assessments.filter(a => a.status === 'completed').sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))
          const active = assessments.filter(a => a.status === 'in_progress')
          return (completed[0] || active[0])?.id
        })()}
        businessScoping={responses?.businessScoping}
        onVolumesChange={handleVolumesChange}
      />

      )}

      {/* Downtime Analysis tab (Financial Inputs + Downtime Cost) */}
      {impactTab === 'financial' && (
      <>
      <div className="card p-6">
        <div className="border-b border-gray-100 dark:border-gray-700/30 pb-3 mb-4">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Financial Inputs</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Estimated Annual Revenue
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
              <input
                type="text"
                inputMode="numeric"
                value={formatNumberWithCommas(impactData.estimated_annual_revenue)}
                onChange={(e) => handleInputChange('estimated_annual_revenue', parseFormattedNumber(e.target.value))}
                placeholder="5,000,000"
                className="w-full pl-8 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Used for downtime cost estimation and loss scenario calculations
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Number of Employees
            </label>
            <input
              type="text"
              inputMode="numeric"
              value={formatNumberWithCommas(impactData.employee_count)}
              onChange={(e) => handleInputChange('employee_count', parseFormattedNumber(e.target.value))}
              placeholder="50"
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Total employee headcount for productivity loss calculations
            </p>
          </div>
        </div>
      </div>


      {/* Downtime Cost — merged into Financial/Downtime tab */}
      <div className="card p-6">
        <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-700/30 pb-3 mb-4">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Downtime Cost</h3>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Estimated cost per hour when critical systems are unavailable</p>
          </div>
          <button
            onClick={() => setShowDowntimeHelper(!showDowntimeHelper)}
            className="text-xs text-primary-600 dark:text-accent-300 hover:underline"
          >
            {showDowntimeHelper ? 'Hide Helper' : 'Estimate for Me'}
          </button>
        </div>

        {showDowntimeHelper && estimatedDowntime && (
          <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <h4 className="font-medium text-yellow-800 dark:text-yellow-200 mb-2">
              Estimated Downtime Cost
            </h4>
            <div className="grid grid-cols-2 gap-4 text-sm mb-3">
              <div>
                <span className="text-gray-600 dark:text-gray-400">Revenue component:</span>
                <span className="ml-2 font-medium text-gray-900 dark:text-white">
                  ${estimatedDowntime.revenueComponent.toLocaleString()}/hr
                </span>
              </div>
              <div>
                <span className="text-gray-600 dark:text-gray-400">Labor component:</span>
                <span className="ml-2 font-medium text-gray-900 dark:text-white">
                  ${estimatedDowntime.laborComponent.toLocaleString()}/hr
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <span className="text-yellow-800 dark:text-yellow-200 font-medium">Suggested:</span>
                <span className="ml-2 text-lg font-bold text-yellow-900 dark:text-yellow-100">
                  ${estimatedDowntime.estimated.toLocaleString()}/hour
                </span>
              </div>
              <button
                onClick={applyEstimatedDowntime}
                className="px-3 py-1 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700 transition-colors"
              >
                Use This Value
              </button>
            </div>
            <p className="mt-2 text-xs text-yellow-700 dark:text-yellow-300">
              {estimatedDowntime.methodology}
            </p>
          </div>
        )}

        {/* Critical Systems BIA Summary */}
        {criticalSystems.length > 0 && (
          <div className="mb-5 p-4 bg-primary-50 dark:bg-primary-900/10 border border-primary-200 dark:border-primary-800/30 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-medium text-gray-800 dark:text-gray-200">
                Per-System Impact Analysis
              </h4>
              {systemsDerivedCost && !impactData.downtime_cost_per_hour && (
                <button
                  onClick={() => handleInputChange('downtime_cost_per_hour', String(systemsDerivedCost))}
                  className="text-xs font-medium text-primary-600 dark:text-primary-400 hover:underline"
                >
                  Use weighted average (${systemsDerivedCost.toLocaleString()}/hr)
                </button>
              )}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-gray-500 dark:text-gray-400">
                    <th className="text-left pb-2 font-medium">System</th>
                    <th className="text-left pb-2 font-medium">Criticality</th>
                    <th className="text-right pb-2 font-medium">RTO</th>
                    <th className="text-right pb-2 font-medium">$/Hour</th>
                    <th className="text-right pb-2 font-medium">Rev. Dep.</th>
                  </tr>
                </thead>
                <tbody>
                  {criticalSystems
                    .sort((a, b) => {
                      const order = { critical: 0, high: 1, moderate: 2, low: 3 }
                      return (order[a.criticality_tier] ?? 2) - (order[b.criticality_tier] ?? 2)
                    })
                    .map(s => (
                    <tr key={s.id} className="border-t border-primary-100 dark:border-primary-800/20">
                      <td className="py-1.5 text-gray-800 dark:text-gray-200 font-medium">
                        {s.name}
                        {s.ai_estimated && <span className="ml-1 text-[10px] text-amber-500">AI</span>}
                      </td>
                      <td className="py-1.5 text-gray-600 dark:text-gray-400 capitalize">{s.criticality_tier}</td>
                      <td className="py-1.5 text-right text-gray-600 dark:text-gray-400">
                        {s.rto_hours != null ? (s.rto_hours < 1 ? `${Math.round(s.rto_hours * 60)}min` : `${s.rto_hours}h`) : '—'}
                      </td>
                      <td className="py-1.5 text-right text-gray-800 dark:text-gray-200 font-medium">
                        ${(s.hourly_cost_impact || 0).toLocaleString()}
                      </td>
                      <td className="py-1.5 text-right text-gray-600 dark:text-gray-400">
                        {s.revenue_dependency_pct != null ? `${s.revenue_dependency_pct}%` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {systemsDerivedCost && (
              <div className="mt-2 pt-2 border-t border-primary-200 dark:border-primary-800/30 flex justify-between text-xs">
                <span className="text-gray-600 dark:text-gray-400">Weighted average hourly impact</span>
                <span className="font-medium text-gray-800 dark:text-gray-200">${systemsDerivedCost.toLocaleString()}/hr</span>
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Cost Per Hour
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
              <input
                type="text"
                inputMode="numeric"
                value={formatNumberWithCommas(impactData.downtime_cost_per_hour)}
                onChange={(e) => handleInputChange('downtime_cost_per_hour', parseFormattedNumber(e.target.value))}
                placeholder="5,000"
                className="w-full pl-8 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              />
              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">/hour</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Recovery Time Objective (RTO)
            </label>
            <select
              value={impactData.recovery_time_objective}
              onChange={(e) => handleInputChange('recovery_time_objective', e.target.value)}
              className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">Select target recovery time...</option>
              <option value="4">4 hours</option>
              <option value="8">8 hours</option>
              <option value="24">24 hours (1 day)</option>
              <option value="48">48 hours (2 days)</option>
              <option value="72">72+ hours (3+ days)</option>
            </select>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Maximum acceptable time to restore critical systems after an incident
            </p>
          </div>
        </div>
      </div>

      </>
      )}

      {/* Cyber Insurance tab */}
      {impactTab === 'insurance' && (
      <>
      {/* Cyber Insurance */}
      <div className="card p-6">
        <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-700/30 pb-3 mb-4">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Cyber Insurance Coverage</h3>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Insurance coverage affects your net exposure in loss scenarios</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={impactData.has_cyber_insurance}
              onChange={(e) => handleInputChange('has_cyber_insurance', e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 dark:peer-focus:ring-primary-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-green-500"></div>
            <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-300">
              {impactData.has_cyber_insurance ? 'Yes' : 'No'}
            </span>
          </label>
        </div>

        {impactData.has_cyber_insurance && (
          <div className="space-y-6 mt-4">
            {/* Carrier Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Insurance Carrier
              </label>
              <select
                value={impactData.insurance_carrier}
                onChange={(e) => handleInputChange('insurance_carrier', e.target.value)}
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {INSURANCE_CARRIERS.map((carrier) => (
                  <option key={carrier.value} value={carrier.value}>
                    {carrier.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Select your cyber insurance provider
              </p>
            </div>

            {/* Other Carrier Input - shown when "Other" is selected */}
            {impactData.insurance_carrier === 'other' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Carrier Name
                </label>
                <input
                  type="text"
                  value={impactData.insurance_carrier_other}
                  onChange={(e) => handleInputChange('insurance_carrier_other', e.target.value)}
                  placeholder="Enter carrier name"
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Coverage Limit
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={formatNumberWithCommas(impactData.insurance_coverage_limit)}
                  onChange={(e) => handleInputChange('insurance_coverage_limit', parseFormattedNumber(e.target.value))}
                  placeholder="1,000,000"
                  className="w-full pl-8 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Maximum coverage per incident
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Client Retention
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={formatNumberWithCommas(impactData.client_retention)}
                  onChange={(e) => handleInputChange('client_retention', parseFormattedNumber(e.target.value))}
                  placeholder="25,000"
                  className="w-full pl-8 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Your share of loss before coverage applies (aka deductible/SIR)
              </p>
            </div>
            </div>
            <details className="mt-6 group">
              <summary className="cursor-pointer text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 select-none">
                <span className="inline-block transition-transform group-open:rotate-90 mr-1">▸</span>
                Advanced: Recommendation confidence level
              </summary>
              <div className="mt-3 pl-4 border-l-2 border-gray-200 dark:border-gray-700">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Confidence threshold for coverage recommendation
                </label>
                <select
                  value={impactData.recommendation_exceedance_target || '0.05'}
                  onChange={(e) => handleInputChange('recommendation_exceedance_target', e.target.value)}
                  className="w-full md:w-1/2 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="0.10">P90 — 10% breach probability (lower coverage target)</option>
                  <option value="0.05">P95 — 5% breach probability (default, industry standard)</option>
                  <option value="0.01">P99 — 1% breach probability (highly conservative)</option>
                </select>
                <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                  Sets the modeled annual-loss percentile that anchors the recommended coverage.
                  P95 is the cyber actuarial default. Choose P90 for cost-conscious clients comfortable
                  with a 1-in-10 breach risk; choose P99 for highly regulated or low-tolerance clients.
                </p>
              </div>
            </details>
          </div>
        )}
      </div>

      {/* Cyber Insurance Policy Review — upload, list, AI review (Phase B) */}
      {client?.id && <CyberInsurancePoliciesSection client={client} />}

      </>
      )}

      </>
      )}

      {/* Estimated Incident Costs + Loss Scenarios — shown ONLY when showOnly='lossScenarios' */}
      {showOnly === 'lossScenarios' && (
      <>
      {/* Estimated Incident Costs (Industry Benchmarks) */}
      {selectedProfile?.regulatoryContext?.breachNotification?.estimatedCosts && (
        <div className="card p-6">
          <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-700/30 pb-3 mb-3">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Estimated Incident Costs</h3>
            <span className="text-[10px] text-gray-400 dark:text-gray-500 bg-gray-50 dark:bg-gray-700/50 px-2 py-0.5 rounded">
              {selectedProfile.verticalName} benchmarks
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
            {selectedProfile.regulatoryContext.breachNotification.estimatedCosts.notificationPerRecord && (
              <div className="flex items-baseline space-x-1.5">
                <span className="text-lg font-bold text-gray-900 dark:text-white">
                  ${selectedProfile.regulatoryContext.breachNotification.estimatedCosts.notificationPerRecord}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">/record notification</span>
              </div>
            )}
            {selectedProfile.regulatoryContext.breachNotification.estimatedCosts.forensicsBase && (
              <div className="flex items-baseline space-x-1.5">
                <span className="text-lg font-bold text-gray-900 dark:text-white">
                  ${(selectedProfile.regulatoryContext.breachNotification.estimatedCosts.forensicsBase / 1000).toFixed(0)}K
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">forensics</span>
              </div>
            )}
            {selectedProfile.regulatoryContext.breachNotification.estimatedCosts.legalCounsel && (
              <div className="flex items-baseline space-x-1.5">
                <span className="text-lg font-bold text-gray-900 dark:text-white">
                  ${(selectedProfile.regulatoryContext.breachNotification.estimatedCosts.legalCounsel / 1000).toFixed(0)}K
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">legal</span>
              </div>
            )}
            {selectedProfile.regulatoryContext.breachNotification.estimatedCosts.creditMonitoring && (
              <div className="flex items-baseline space-x-1.5">
                <span className="text-lg font-bold text-gray-900 dark:text-white">
                  ${selectedProfile.regulatoryContext.breachNotification.estimatedCosts.creditMonitoring}
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">/person monitoring</span>
              </div>
            )}
            {selectedProfile.regulatoryContext.breachNotification.estimatedCosts.businessInterruption && (
              <div className="flex items-baseline space-x-1.5">
                <span className="text-lg font-bold text-gray-900 dark:text-white">
                  ${(selectedProfile.regulatoryContext.breachNotification.estimatedCosts.businessInterruption / 1000).toFixed(0)}K
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400">interruption</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Loss Scenario Preview - New Expandable List Design */}
      {lossPreview && lossPreview.scenarios.length > 0 && (
        <div className="card p-6">
          <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-700/30 pb-3 mb-4">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Loss Scenario Analysis</h3>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Select scenarios to include in aggregate calculations. Click any scenario for details.</p>
            </div>
            {/* Summary Badges */}
            <div className="flex items-center space-x-2">
              {lossPreview.summary.criticalCount > 0 && (
                <span className="px-2 py-1 text-xs font-medium bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300 rounded-full">
                  {lossPreview.summary.criticalCount} Critical
                </span>
              )}
              {lossPreview.summary.highCount > 0 && (
                <span className="px-2 py-1 text-xs font-medium bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 rounded-full">
                  {lossPreview.summary.highCount} High
                </span>
              )}
            </div>
          </div>

          {/* Select All / Deselect All Control */}
          <div className="flex items-center justify-between mb-3 pb-3 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={handleSelectAll}
              className="flex items-center space-x-2 text-sm font-medium text-primary-600 dark:text-accent-300 hover:text-primary-700 dark:hover:text-accent-200 transition-colors"
            >
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                Object.keys(selectedScenarios).length === lossPreview.scenarios.length
                  ? 'bg-primary-600 border-primary-600'
                  : Object.keys(selectedScenarios).length > 0
                    ? 'bg-primary-200 border-primary-600 dark:bg-primary-800'
                    : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-500'
              }`}>
                {Object.keys(selectedScenarios).length === lossPreview.scenarios.length ? (
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                  </svg>
                ) : Object.keys(selectedScenarios).length > 0 ? (
                  <svg className="w-3 h-3 text-primary-600 dark:text-accent-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M20 12H4" />
                  </svg>
                ) : null}
              </div>
              <span>
                {Object.keys(selectedScenarios).length === lossPreview.scenarios.length
                  ? 'Deselect All'
                  : Object.keys(selectedScenarios).length === 0
                    ? 'Select All'
                    : 'Select All'}
              </span>
            </button>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {selectedSummary?.selectedCount || 0} of {selectedSummary?.totalCount || 0} selected
            </span>
          </div>

          {/* Scenario List */}
          <div className="space-y-3">
            {lossPreview.scenarios.map(scenario => (
              <ScenarioCard
                key={scenario.scenarioId}
                scenario={scenario}
                hasInsurance={impactData.has_cyber_insurance}
                isExpanded={expandedScenario === scenario.scenarioId}
                onToggle={() => toggleScenario(scenario.scenarioId)}
                isSelected={!!selectedScenarios[scenario.scenarioId]}
                onSelectToggle={handleSelectToggle}
              />
            ))}
          </div>

          {/* Aggregate Summary */}
          <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
            {/* Selection indicator */}
            {selectedSummary && selectedSummary.selectedCount < selectedSummary.totalCount && (
              <div className="mb-3 pb-3 border-b border-gray-200 dark:border-gray-700">
                <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center">
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Showing totals for {selectedSummary.selectedCount} of {selectedSummary.totalCount} scenarios
                </p>
              </div>
            )}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Total Gross ALE</div>
                <div className="text-xl font-bold text-gray-900 dark:text-white mt-1">
                  {formatCurrency(selectedSummary?.totalGrossALE || 0)}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Total Net ALE</div>
                <div className="text-xl font-bold text-primary-600 dark:text-accent-300 mt-1">
                  {formatCurrency(selectedSummary?.totalNetALE || 0)}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Max Single Loss (P90)</div>
                <div className="text-xl font-bold text-red-600 dark:text-red-400 mt-1">
                  {formatCurrency(selectedSummary?.maxSingleLoss || 0)}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Insurance Gap</div>
                <div className="text-xl font-bold text-orange-600 dark:text-orange-400 mt-1">
                  {formatCurrency(selectedSummary?.insuranceGap || 0)}
                </div>
              </div>
            </div>

            {/* Context Factors Applied */}
            {lossPreview.summary.contextFactorsApplied && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <span className="font-medium">Factors applied:</span>
                  <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">
                    {selectedProfile?.verticalName || 'Default'} profile
                  </span>
                  {lossPreview.summary.contextFactorsApplied.orgCharacteristics > 0 && (
                    <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 rounded">
                      {lossPreview.summary.contextFactorsApplied.orgCharacteristics} org traits
                    </span>
                  )}
                  {lossPreview.summary.contextFactorsApplied.frameworks > 0 && (
                    <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 rounded">
                      {lossPreview.summary.contextFactorsApplied.frameworks} frameworks
                    </span>
                  )}
                  {lossPreview.summary.marketSegmentLabel && (
                    <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300 rounded">
                      {lossPreview.summary.marketSegmentLabel}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      </>
      )}

      {/* Ruin Theory — shown ONLY when showOnly='ruinTheory' */}
      {showOnly === 'ruinTheory' && (
      <>
      {/* Ruin Theory Analysis Panel */}
      {lossPreview && lossPreview.scenarios.length > 0 && (
        <div className="card p-6">
          <div className="flex items-center justify-between border-b border-gray-100 dark:border-gray-700/30 pb-3 mb-4">
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Ruin Theory Analysis</h3>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Probability of financial distress from cumulative cyber incidents over time</p>
            </div>
            {ruinRiskLevel && (
              <span className={`px-3 py-1.5 text-sm font-bold rounded-full ${
                ruinRiskLevel.color === 'red' ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' :
                ruinRiskLevel.color === 'orange' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300' :
                ruinRiskLevel.color === 'yellow' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300' :
                'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
              }`}>
                {ruinRiskLevel.level} Risk
              </span>
            )}
          </div>

          {/* Input Parameters */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Cyber Reserves / Emergency Fund
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={formatNumberWithCommas(impactData.cyber_reserves)}
                  onChange={(e) => handleInputChange('cyber_reserves', parseFormattedNumber(e.target.value))}
                  placeholder="100,000"
                  className="w-full pl-8 p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Available budget/reserves for cyber incident response
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Planning Horizon
              </label>
              <select
                value={impactData.ruin_time_horizon}
                onChange={(e) => handleInputChange('ruin_time_horizon', e.target.value)}
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="1">1 Year</option>
                <option value="3">3 Years</option>
                <option value="5">5 Years</option>
                <option value="10">10 Years</option>
              </select>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Time period for ruin probability calculation
              </p>
            </div>
          </div>

          {ruinAnalysis?.canCalculate ? (
            <>
              {/* Primary Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className={`p-4 rounded-lg text-center ${
                  ruinAnalysis.ruinProbability >= 25 ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800' :
                  ruinAnalysis.ruinProbability >= 10 ? 'bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800' :
                  ruinAnalysis.ruinProbability >= 5 ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800' :
                  'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                }`}>
                  <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Ruin Probability</div>
                  <div className={`text-2xl font-bold mt-1 ${
                    ruinAnalysis.ruinProbability >= 25 ? 'text-red-600 dark:text-red-400' :
                    ruinAnalysis.ruinProbability >= 10 ? 'text-orange-600 dark:text-orange-400' :
                    ruinAnalysis.ruinProbability >= 5 ? 'text-yellow-600 dark:text-yellow-400' :
                    'text-green-600 dark:text-green-400'
                  }`}>
                    {ruinAnalysis.ruinProbability.toFixed(1)}%
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    over {ruinAnalysis.timeHorizonYears} year{ruinAnalysis.timeHorizonYears > 1 ? 's' : ''}
                  </div>
                </div>

                <div className="p-4 rounded-lg text-center bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                  <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Survival Probability</div>
                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400 mt-1">
                    {ruinAnalysis.survivalProbability.toFixed(1)}%
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    financial resilience
                  </div>
                </div>

                <div className="p-4 rounded-lg text-center bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
                  <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Effective Surplus</div>
                  <div className="text-2xl font-bold text-purple-600 dark:text-purple-400 mt-1">
                    {formatCurrency(ruinAnalysis.effectiveSurplus)}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    reserves + coverage
                  </div>
                </div>

                <div className={`p-4 rounded-lg text-center ${
                  ruinAnalysis.isUnderfunded
                    ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                    : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                }`}>
                  <div className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">Safety Margin</div>
                  <div className={`text-2xl font-bold mt-1 ${
                    ruinAnalysis.isUnderfunded ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'
                  }`}>
                    {ruinAnalysis.loadingRatio >= 0 ? '+' : ''}{(ruinAnalysis.loadingRatio * 100).toFixed(0)}%
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {ruinAnalysis.isUnderfunded ? 'underfunded' : 'above expected'}
                  </div>
                </div>
              </div>

              {/* Capital Adequacy Assessment */}
              <div className={`p-4 rounded-lg mb-6 ${
                ruinAnalysis.capitalAdequacy.color === 'red' ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800' :
                ruinAnalysis.capitalAdequacy.color === 'orange' ? 'bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800' :
                ruinAnalysis.capitalAdequacy.color === 'yellow' ? 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800' :
                'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
              }`}>
                <div className="flex items-start">
                  <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center mr-3 ${
                    ruinAnalysis.capitalAdequacy.color === 'red' ? 'bg-red-100 dark:bg-red-900/40' :
                    ruinAnalysis.capitalAdequacy.color === 'orange' ? 'bg-orange-100 dark:bg-orange-900/40' :
                    ruinAnalysis.capitalAdequacy.color === 'yellow' ? 'bg-yellow-100 dark:bg-yellow-900/40' :
                    'bg-green-100 dark:bg-green-900/40'
                  }`}>
                    {ruinAnalysis.capitalAdequacy.color === 'green' ? (
                      <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    ) : (
                      <svg className={`w-5 h-5 ${
                        ruinAnalysis.capitalAdequacy.color === 'red' ? 'text-red-600 dark:text-red-400' :
                        ruinAnalysis.capitalAdequacy.color === 'orange' ? 'text-orange-600 dark:text-orange-400' :
                        'text-yellow-600 dark:text-yellow-400'
                      }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <h4 className={`font-semibold ${
                      ruinAnalysis.capitalAdequacy.color === 'red' ? 'text-red-800 dark:text-red-300' :
                      ruinAnalysis.capitalAdequacy.color === 'orange' ? 'text-orange-800 dark:text-orange-300' :
                      ruinAnalysis.capitalAdequacy.color === 'yellow' ? 'text-yellow-800 dark:text-yellow-300' :
                      'text-green-800 dark:text-green-300'
                    }`}>
                      Capital Adequacy: {ruinAnalysis.capitalAdequacy.level}
                    </h4>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {ruinAnalysis.capitalAdequacy.recommendation}
                    </p>
                  </div>
                </div>
              </div>

              {/* Required Reserves Table */}
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mb-6">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">
                  Required Reserves by Target Survival Rate
                </h4>
                <div className="grid grid-cols-3 gap-4">
                  <div className="text-center p-3 bg-white dark:bg-gray-700 rounded-lg">
                    <div className="text-xs text-gray-500 dark:text-gray-400">90% Survival</div>
                    <div className="text-lg font-bold text-gray-900 dark:text-white mt-1">
                      {ruinAnalysis.requiredReserves.for90Percent < Infinity
                        ? formatCurrency(ruinAnalysis.requiredReserves.for90Percent)
                        : 'N/A'}
                    </div>
                  </div>
                  <div className="text-center p-3 bg-white dark:bg-gray-700 rounded-lg">
                    <div className="text-xs text-gray-500 dark:text-gray-400">95% Survival</div>
                    <div className="text-lg font-bold text-gray-900 dark:text-white mt-1">
                      {ruinAnalysis.requiredReserves.for95Percent < Infinity
                        ? formatCurrency(ruinAnalysis.requiredReserves.for95Percent)
                        : 'N/A'}
                    </div>
                  </div>
                  <div className="text-center p-3 bg-white dark:bg-gray-700 rounded-lg border-2 border-primary-200 dark:border-primary-700">
                    <div className="text-xs text-primary-600 dark:text-accent-300 font-medium">99% Survival</div>
                    <div className="text-lg font-bold text-primary-600 dark:text-accent-300 mt-1">
                      {ruinAnalysis.requiredReserves.for99Percent < Infinity
                        ? formatCurrency(ruinAnalysis.requiredReserves.for99Percent)
                        : 'N/A'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Materiality Analysis */}
              {ruinAnalysis.materialityAnalysis && (
                <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">
                    Revenue Impact Analysis
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className={`p-3 rounded-lg ${
                      ruinAnalysis.materialityAnalysis.isMaterial
                        ? 'bg-amber-50 dark:bg-amber-900/20'
                        : 'bg-white dark:bg-gray-700'
                    }`}>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Expected Annual Loss (ALE)</div>
                      <div className="flex items-baseline mt-1">
                        <span className={`text-xl font-bold ${
                          ruinAnalysis.materialityAnalysis.isMaterial
                            ? 'text-amber-600 dark:text-amber-400'
                            : 'text-gray-900 dark:text-white'
                        }`}>
                          {ruinAnalysis.materialityAnalysis.aleAsPercentRevenue}%
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">of revenue</span>
                      </div>
                      {ruinAnalysis.materialityAnalysis.isMaterial && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">Material impact threshold exceeded</p>
                      )}
                    </div>
                    <div className={`p-3 rounded-lg ${
                      ruinAnalysis.materialityAnalysis.isExistential
                        ? 'bg-red-50 dark:bg-red-900/20'
                        : 'bg-white dark:bg-gray-700'
                    }`}>
                      <div className="text-xs text-gray-500 dark:text-gray-400">Max Single Loss</div>
                      <div className="flex items-baseline mt-1">
                        <span className={`text-xl font-bold ${
                          ruinAnalysis.materialityAnalysis.isExistential
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-gray-900 dark:text-white'
                        }`}>
                          {ruinAnalysis.materialityAnalysis.maxLossAsPercentRevenue}%
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">of revenue</span>
                      </div>
                      {ruinAnalysis.materialityAnalysis.isExistential && (
                        <p className="text-xs text-red-600 dark:text-red-400 mt-1">Potentially existential threat</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Time to Ruin Warning */}
              {ruinAnalysis.expectedTimeToRuin !== null && ruinAnalysis.expectedTimeToRuin < 10 && (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg mb-6">
                  <div className="flex items-start">
                    <svg className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <h4 className="font-semibold text-red-800 dark:text-red-300">
                        Accelerated Depletion Warning
                      </h4>
                      <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                        At current funding levels relative to expected losses, reserves could be depleted in approximately{' '}
                        <strong>{ruinAnalysis.expectedTimeToRuin.toFixed(1)} years</strong>. Consider increasing cyber budget or transferring risk through additional insurance coverage.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Scenario Probabilities */}
              <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 uppercase tracking-wide">
                  {ruinAnalysis.timeHorizonYears}-Year Incident Probability
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  <div className="p-2 bg-white dark:bg-gray-700 rounded text-center">
                    <div className="text-xs text-gray-500 dark:text-gray-400">No Major Incident</div>
                    <div className="text-sm font-bold text-green-600 dark:text-green-400">
                      {(ruinAnalysis.scenarioAnalysis.noSignificantLoss * 100).toFixed(0)}%
                    </div>
                  </div>
                  <div className="p-2 bg-white dark:bg-gray-700 rounded text-center">
                    <div className="text-xs text-gray-500 dark:text-gray-400">Moderate Loss</div>
                    <div className="text-sm font-bold text-yellow-600 dark:text-yellow-400">
                      {(ruinAnalysis.scenarioAnalysis.moderateLoss * 100).toFixed(0)}%
                    </div>
                  </div>
                  <div className="p-2 bg-white dark:bg-gray-700 rounded text-center">
                    <div className="text-xs text-gray-500 dark:text-gray-400">Severe Loss</div>
                    <div className="text-sm font-bold text-orange-600 dark:text-orange-400">
                      {(ruinAnalysis.scenarioAnalysis.severeLoss * 100).toFixed(0)}%
                    </div>
                  </div>
                  <div className="p-2 bg-white dark:bg-gray-700 rounded text-center">
                    <div className="text-xs text-gray-500 dark:text-gray-400">Catastrophic</div>
                    <div className="text-sm font-bold text-red-600 dark:text-red-400">
                      {(ruinAnalysis.scenarioAnalysis.catastrophic * 100).toFixed(0)}%
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="p-6 text-center text-gray-500 dark:text-gray-400">
              <svg className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
              <p className="font-medium">Complete Loss Scenario Analysis First</p>
              <p className="text-sm mt-1">
                {ruinAnalysis?.reason || 'Enter organization profile data and review loss scenarios to enable ruin analysis.'}
              </p>
            </div>
          )}
        </div>
      )}
      </>
      )}
    </div>
  )
}

export default ImpactCalibrationStep
